// Photo configuration with folder-based auto-categorization
// Images should be organized in folders: /photos/{category}/{filename}
// The category is automatically extracted from the folder path

export interface PhotoConfig {
  src: string; // Path relative to public folder, includes category folder
  caption: string;
  date: string;
  category?: string; // Optional: manually specify category, otherwise auto-detect from path
}

// Helper function to extract category from path
export const getCategoryFromPath = (path: string): string => {
  // Extract category from path like: /photos/trips/image.jpg -> 'Trips'
  const match = path.match(/\/photos\/([^/]+)\//);
  if (match) {
    const categoryFolder = match[1];
    // Convert folder name to category name (capitalize first letter of each word)
    return categoryFolder
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
  
  // Fallback: try to detect from filename or return 'Uploaded'
  const lowerPath = path.toLowerCase();
  if (lowerPath.includes('trip') || lowerPath.includes('travel') || lowerPath.includes('vacation')) {
    return 'Trips';
  }
  if (lowerPath.includes('prewedding') || lowerPath.includes('engagement') || lowerPath.includes('proposal')) {
    return 'Pre-wedding';
  }
  if (lowerPath.includes('family') || lowerPath.includes('parents') || lowerPath.includes('siblings')) {
    return 'Family';
  }
  if (lowerPath.includes('candid') || lowerPath.includes('fun') || lowerPath.includes('spontaneous')) {
    return 'Candid Moments';
  }
  return 'Uploaded';
};

// Helper to check if image exists and fallback to root if needed
export const resolveImagePath = (path: string): string => {
  // If path already includes /photos/, use it as-is
  if (path.includes('/photos/')) {
    return path;
  }
  // Otherwise, assume it's in root and return as-is
  return path;
};

// Photo configurations organized by category folders
// To add new photos, just add them to the appropriate category array
// The category will be automatically determined from the folder structure
//
// IMPORTANT: Organize your images in folders:
//   - /public/photos/trips/        -> Auto-categorized as "Trips"
//   - /public/photos/pre-wedding/   -> Auto-categorized as "Pre-wedding"
//   - /public/photos/family/        -> Auto-categorized as "Family"
//   - /public/photos/candid-moments/ -> Auto-categorized as "Candid Moments"
//
// Just add the path here, and the category will be automatically determined!

export const photosConfig: PhotoConfig[] = [
  // Trips category - images in /photos/trips/
  // Category is automatically detected from folder path!
  {
    src: '/photos/trips/gallery1.jpg',
    caption: 'Our first mountain adventure together',
    date: 'July 2022'
  },
  {
    src: '/photos/trips/gallery2.jpg',
    caption: 'Sunset by the lake',
    date: 'August 2022'
  },
  {
    src: '/photos/trips/gallery3.jpg',
    caption: 'Beach memories',
    date: 'September 2022'
  },
  
  // Pre-wedding category - images in /photos/pre-wedding/
  // Category is automatically detected from folder path!
  {
    src: '/photos/pre-wedding/gallery4.jpg',
    caption: 'The proposal moment',
    date: 'September 2024'
  },
  {
    src: '/photos/pre-wedding/gallery5.jpg',
    caption: 'Engagement photoshoot',
    date: 'October 2024'
  },
  {
    src: '/photos/pre-wedding/gallery6.jpg',
    caption: 'Romantic evening stroll',
    date: 'November 2024'
  },
  
  // Family category - images in /photos/family/
  // Category is automatically detected from folder path!
  {
    src: '/photos/family/gallery7.jpg',
    caption: 'Meeting the parents',
    date: 'December 2022'
  },
  {
    src: '/photos/family/gallery8.jpg',
    caption: 'Our first home together',
    date: 'June 2023'
  },
  
  // Candid Moments category - images in /photos/candid-moments/
  // To add new photos, just use the folder path - category will be auto-detected!
  // Example:
  // {
  //   src: '/photos/candid-moments/fun-moment.jpg',
  //   caption: 'Spontaneous laughter',
  //   date: 'January 2023'
  // },
];

// Helper function to generate Photo objects with auto-categorized categories
export const generatePhotos = (): Array<{ id: number; src: string; category: string; caption: string; date: string }> => {
  return photosConfig.map((photo, index) => ({
    id: index + 1,
    src: photo.src,
    category: photo.category || getCategoryFromPath(photo.src), // Use manual category if provided, otherwise auto-detect
    caption: photo.caption,
    date: photo.date
  }));
};

